import { createAsyncThunk } from '@reduxjs/toolkit';
import { ConversationStarter, QueryParams, ConversationStarterCategory } from '../../types';
import { Paginated, PaginationService } from '../../services/api-handlers/pagination';
import { ConversationStarterCategoryApi } from '../../services/end-points';
import { resolveApiCall } from '../../services/api-handlers/api-resolver';
import SortingService from '../../services/sorting';
import { ConversationStarterFormData } from '../../screens/ConversationStarter/components/ConversationStarterForm';
import { makeFormErrorsFromResponse } from '../../components/react-hook-form/utils/make-form-errors';
import { ThunkConfig } from '../configure-store';

export const listConversationCategory = createAsyncThunk<
    Paginated<ConversationStarterCategory>,
    QueryParams,
    ThunkConfig
>('conversation-starter-category/list', async (payload, thunkApi) => {
    const { getState } = thunkApi;
    const { conversationStarterCategory } = getState();
    const { filters, sorting, ...rest } = payload;

    return PaginationService.resolveApiCall(
        thunkApi,
        conversationStarterCategory,
        async () =>
            ConversationStarterCategoryApi.list({
                ...filters,
                ...SortingService.makeOrder(sorting),
                ...rest,
            }),
        payload.page,
        conversationStarterCategory.data,
    );
});

export const getAllConversationCategory = createAsyncThunk<
    Paginated<ConversationStarterCategory>,
    undefined,
    ThunkConfig
>('conversation-starter-category/all-list', async (payload, thunkApi) => {
    const { getState } = thunkApi;
    const { conversationStarterCategory } = getState();

    return PaginationService.resolveApiCall(
        thunkApi,
        conversationStarterCategory,
        async () =>
            ConversationStarterCategoryApi.list({
                limit: -1,
            }),
        1,
        {
            list: [],
            page: 1,
        },
    );
});

export const getConversationCategory = createAsyncThunk<
    ConversationStarterCategory,
    { id: string; onError: (data: any) => any },
    ThunkConfig
>('conversation-starter-category/current', async (payload, thunkApi) => {
    const { getState } = thunkApi;
    const { conversationStarterCategory } = getState();
    const { id, onError } = payload;

    return resolveApiCall(
        thunkApi,
        conversationStarterCategory,
        async () => (await ConversationStarterCategoryApi.get(id)).data,
        onError,
    );
});

export const deleteConversationCategory = createAsyncThunk<any, { id: string; callback: () => void }, ThunkConfig>(
    'conversation-starter-category/delete',
    async (payload, thunkApi) => {
        const { getState } = thunkApi;
        const { id } = payload;
        const { conversationStarterCategory } = getState();
        await resolveApiCall(
            thunkApi,
            conversationStarterCategory,
            async () => (
                await ConversationStarterCategoryApi.remove(id),
                async (err: { response: any }) => {
                    const { response } = err;
                    return makeFormErrorsFromResponse(response.data);
                }
            ),
        );
        payload.callback();
    },
);

export const createConversationCategory = createAsyncThunk<
    ConversationStarterCategory,
    ConversationStarterFormData,
    ThunkConfig
>('conversation-starter-category/create', async (payload, thunkApi) => {
    const { getState } = thunkApi;
    const { conversationStarterCategory } = getState();
    return resolveApiCall(
        thunkApi,
        conversationStarterCategory,
        async () => await ConversationStarterCategoryApi.create(payload),
        async (err) => {
            const { response } = err;
            return makeFormErrorsFromResponse(response.data);
        },
    );
});

export const updateConversationCategory = createAsyncThunk<
    ConversationStarterCategory,
    { id: ConversationStarter['id']; data: Partial<ConversationStarterFormData> },
    ThunkConfig
>('conversation-starter-category/update', async (payload, thunkApi) => {
    const { getState } = thunkApi;
    const { conversationStarterCategory } = getState();
    const { id, data } = payload;
    return resolveApiCall(
        thunkApi,
        conversationStarterCategory,
        async () => await ConversationStarterCategoryApi.update(id, data),
        async (err) => {
            const { response } = err;
            return makeFormErrorsFromResponse(response.data);
        },
    );
});
